import { useEffect, useState } from "react";
import { Typography, Card, Box, useMediaQuery, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRecordContext, useDataProvider, Loading } from "react-admin";
import AnimatedBlock from "../../../components/Common/AnimatedBlock";
import AuditTimeline, { AuditItem, AuditKind } from "../../../components/Audit/AuditTimeline";

const KIND_RULES: [RegExp, AuditKind][] = [
  [/CREATE/i, "created"],
  [/APPROV|REJECT/i, "approval"],
  [/KYT|TRAVEL/i, "kyt"],
  [/BROADCAST|CONFIRM|SEND|EXECUT|TRANSFER/i, "payment"],
  [/BILL|STATEMENT/i, "other"],
];

const statusOf = (state: any) => state?.statusOrder || state?.status;

const isEmpty = (value: any) => value === undefined || value === null || value === "";

// The fields an event changed, as "field: before → after" (status shown apart). A field
// the event only sets is left out when empty: a sent webhook has no error to show.
const changes = (before: any = {}, after: any = {}) =>
  Object.keys({ ...before, ...after })
    .filter((key) => !["statusOrder", "status"].includes(key))
    .filter((key) => JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key]))
    .filter((key) => (before && key in before) || !isEmpty(after?.[key]))
    .map((key) => {
      const show = (value: any) => (value === undefined || value === null ? "—" : typeof value === "object" ? JSON.stringify(value) : String(value));
      return before && key in before ? `${key}: ${show(before[key])} → ${show(after?.[key])}` : `${key}: ${show(after?.[key])}`;
    });

// A webhook delivery as one readable line: the status notified, then the outcome.
const webhookLine = (after: any = {}) =>
  [
    after.webhookStatus,
    after.error || (after.httpStatus ? `HTTP ${after.httpStatus}` : null),
    after.durationMs != null ? `${after.durationMs} ms` : null,
    Number(after.attempt) > 1 ? `attempt ${after.attempt}` : null,
    after.url,
  ].filter((part) => !isEmpty(part));

const toItem = (entry: any): AuditItem => {
  const by = entry.by;
  const isSystem = !by || Boolean(by.system);
  const actor = !by || by.system ? "System" : by.name || by.email || by.userId || "—";
  const action = String(entry.action || "");
  const before = entry.meta?.before;
  const after = entry.meta?.after;
  const from = statusOf(before);
  const to = statusOf(after);
  const title = action ? action.charAt(0) + action.slice(1).toLowerCase().replace(/_/g, " ") : "";
  const details = /^WEBHOOK_/.test(action) ? webhookLine(after) : changes(before, after);
  const text = [title, entry.meta?.text || entry.meta?.reason, ...details].filter(Boolean).join(" · ");
  const kind = KIND_RULES.find(([rule]) => rule.test(action))?.[1] || (from && to ? "status" : "other");
  return { id: entry.id, at: entry.at, kind, actor, isSystem, text, ...(from && to && from !== to ? { from, to } : {}) };
};

function AuditTrailTab() {
  const { t }: { t: any } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  const record = useRecordContext();
  const dataProvider = useDataProvider();

  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record?.id) return;
    setLoading(true);
    dataProvider
      .getList("gasStationTransfersAudit", {
        filter: { id: record.id },
        pagination: { page: 1, perPage: 100 },
        sort: { field: "at", order: "ASC" },
      })
      // The API returns events oldest first; the most recent is shown first.
      .then(({ data }) => setEntries([...data].reverse()))
      .finally(() => setLoading(false));
  }, [dataProvider, record?.id]);

  if (!record) return null;

  return (
    <Box>
      {!isMobile && (
        <Box pb={2}>
          <Typography variant="h6">
            {t("Audit trail")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Every status change and action recorded on this transfer.")}
          </Typography>
        </Box>
      )}

      <AnimatedBlock>
        <Card sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
          {loading ? (
            <Loading />
          ) : entries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("No audit events for this transfer.")}
            </Typography>
          ) : (
            <AuditTimeline items={entries.map(toItem)} />
          )}
        </Card>
      </AnimatedBlock>
    </Box>
  );
}

export default AuditTrailTab;
